import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Grid, InputBase, Modal, Stack, styled, Typography } from '@mui/material';
import { getIdToken } from 'firebase/auth';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { LoadCanvasTemplateNoReload, loadCaptchaEnginge, validateCaptcha } from 'react-simple-captcha';
import Iconify from '../../../../components/Iconify';
import useLocales from '../../../../hooks/useLocales';
import PhonenumberTextField from '../../../../components/PhonenumberTextField';
import RegisterOTPForm from '../visitor/RegisterOTPForm';
import { getOtpErrorMessage } from '../../../../utils/formatString';
import uuidv4 from '../../../../utils/uuidv4';
import { checkIfNumberExist } from '../../../../utils/formatObject';
import conObj from '../../../../utils/connection-assistant';
import palette from '../../../../theme/palette';
import CustomModal from '../../../../components/CustomModal';
import {
  InputBorder,
  InputContainer,
  StyledIconify,
  StyledInput,
  ThemeButton,
} from '../../../../components/settings/GlobalStyled';
import { IS_OTP_READY } from '../../../../config';
import { validMobileNetworks } from '../../../../utils/validMobileNetworks';

const snackbarOptionsSuccess = { variant: 'success', autoHideDuration: 5000 };
const snackbarOptionsError = { variant: 'error', autoHideDuration: 5000 };

const defaultForm = {
  userPhoneNumber: '',
  captcha: '',
};

const ForgotPasswordDialog = ({ openForgotPasswordDialog, setOpenForgotPasswordDialog }) => {
  const navigate = useNavigate();

  const { translate } = useLocales();

  const [error, setError] = useState('');

  const [formError, setFormError] = useState({});

  const [formData, setFormData] = useState({
    userPhoneNumber: '',
    captcha: '',
  });

  // const [isInit, setIsInit] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    // firebaseUtil.initVerifier();
    // setIsInit(firebaseUtil.getIsInitialized());
  }, []);

  useEffect(() => {
    if (openForgotPasswordDialog) {
      setTimeout(() => loadCaptchaEnginge(4), 100);
    }
  }, [openForgotPasswordDialog]);

  const handleFormChange = (e) => {
    // const regex = /^\d+$/;
    // if (e.target.name === 'userPhoneNumber' && e.target.value && !regex.test(e.target.value)) return;

    setFormData((prev) => ({ ...prev, [e?.target?.name]: e?.target?.value }));
  };

  const handleClose = () => {
    setOpenForgotPasswordDialog(false);
    setFormData(defaultForm);
  };

  const isValid = () => {
    const _errors = {};
    let valid = true;

    if (!formData?.userPhoneNumber) {
      valid = false;
      _errors.userPhoneNumber = translate('x_is_required', { x: translate('phone_number') });
    }

    /**
     * Check if it is a valid Nepal number
     *   */
    const validatePhoneNumber = (phoneNumber) => {
      // Check first 4 digits(Network provider)
      const startsWith = phoneNumber.slice(0, 2);

      // check length as well
      if (validMobileNetworks(startsWith) && phoneNumber.length === 10) {
        _errors.userPhoneNumber = '';
      } else {
        valid = false;
        _errors.userPhoneNumber = translate('must_start_with');
      }
    };

    if (formData?.userPhoneNumber.trim()) {
      validatePhoneNumber(formData?.userPhoneNumber.trim());
      // validateNumber();
    } else {
      valid = false;
      _errors.userPhoneNumber = translate('must_start_with');
    }

    if (validateCaptcha(formData?.captcha?.trim()) === false) {
      valid = false;
      setFormData((prev) => ({ ...prev, captcha: '' }));
      _errors.captcha = translate('captcha_not_match');
    }

    console.log(_errors);

    setFormError(_errors);

    return valid;
  };

  const onSubmit = async () => {
    setError('');
    setFormError({});
    try {
      setIsLoading(true);

      if (!isValid()) return;

      const isExists = await checkIfNumberExist(formData?.userPhoneNumber);

      if (!isExists) throw new Error(translate('number_not_exists'));

      let fingerprint = localStorage.getItem('fingerprint');

      if (!fingerprint) {
        fingerprint = uuidv4();
        localStorage.setItem('fingerprint', fingerprint);
      }

      const response = await conObj.post('otp_request.json', {
        no_hp: formData?.userPhoneNumber.replace(/[^0-9]/g, ''),
        fingerprint,
        purpose: 'forgotpassword',
      });

      navigate('/auth/reset-password', {
        state: {
          otpUuid: response.data?.data?.data?.otp_uuid,
          phoneNumber: formData?.userPhoneNumber.replace(/[^0-9]/g, ''),
        },
      });
    } catch (error) {
      console.error(error);
      setError(getOtpErrorMessage(translate, error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CustomModal
      open={openForgotPasswordDialog}
      onClose={() => setOpenForgotPasswordDialog(false)}
      sx={{ overflow: 'hidden' }}
    >
      <Box sx={{ p: 2 }}>
        {!IS_OTP_READY ? (
          <Stack spacing={2} sx={{ px: 2, alignItems: 'center' }}>
            <Iconify icon={'mdi:face-agent'} sx={{ fontSize: '4rem', color: '#000' }} />
            <Typography sx={{ fontWeight: 600, color: 'grey.700', fontSize: '18px', mb: 2, textAlign: 'center' }}>
              {translate('cs_forgot_password')}
            </Typography>
          </Stack>
        ) : (
          <>
            <Typography sx={{ fontWeight: 600, color: '#000', fontSize: '18px', mb: 2 }}>
              {translate('reset_password')}
            </Typography>
            <Box>
              <Box sx={{ mb: 2 }}>
                {error ? (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {error}
                  </Alert>
                ) : (
                  ''
                )}
                <Stack>
                  <InputBorder>
                    <InputContainer direction="row">
                      <StyledIconify icon={'mdi-light:phone'} />
                      <StyledInput
                        name="userPhoneNumber"
                        type="number"
                        value={formData?.userPhoneNumber}
                        onChange={(e) => handleFormChange(e)}
                        placeholder={translate('please_input_your_x', { x: translate('phone_number') })}
                      />
                    </InputContainer>
                  </InputBorder>
                  {formError?.userPhoneNumber && (
                    <span style={{ fontSize: '.75rem', color: 'red' }}>{formError?.userPhoneNumber}</span>
                  )}

                  <Grid container sx={{ mt: 1 }}>
                    <Grid item xs={5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <LoadCanvasTemplateNoReload />
                      </Box>
                    </Grid>
                    <Grid item xs={7}>
                      <InputBorder>
                        <InputContainer direction="row">
                          <StyledInput
                            name="captcha"
                            placeholder="Enter text on the left"
                            value={formData?.captcha}
                            onChange={handleFormChange}
                          />
                        </InputContainer>
                      </InputBorder>
                      {formError?.captcha && (
                        <span style={{ fontSize: '.75rem', color: 'red' }}>{formError?.captcha}</span>
                      )}
                    </Grid>
                  </Grid>
                </Stack>
              </Box>

              <ThemeButton loading={isLoading} onClick={() => onSubmit()}>
                {translate(`confirm`)}
              </ThemeButton>
            </Box>
          </>
        )}
      </Box>
    </CustomModal>
  );
};

ForgotPasswordDialog.propTypes = {
  openForgotPasswordDialog: PropTypes.bool,
  setOpenForgotPasswordDialog: PropTypes.func,
};

export default ForgotPasswordDialog;
